import React, { useState, useEffect } from "react";
import Post, { CommentData } from "../../components/common/Post/Post";
import {
    Box,
    Typography,
    Container,
    Paper,
    Avatar,
    TextField,
    IconButton,
    CircularProgress,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    SelectChangeEvent,
    Button as MuiButton,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import {
    PhotoOutlined,
    VideoCallOutlined,
    AttachFileOutlined,
    Tag,
    Report,
    X,
    FeedbackOutlined,
} from "@mui/icons-material";
import classes from "./Newsfeed.module.scss";
import {
    fetchAllPosts,
    fetchPosts,
    selectNewsfeed,
    addPost,
    updatePost,
    deletePost,
    addPostAsync,
    PostData,
    Comment,
    updatePostAsync,
    selectFilteredPosts,
    selectSearchQuery,
    setSortOption,
    SortOption,
    toggleCategory,
    selectActiveCategories,
    createPost,
    deletePostAsync,
} from "../../store/slices/newsfeedSlice";
import useResponsive from "../../hooks/useResponsive";
import { useDispatch, useSelector, useStore } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { RootState, AppDispatch } from "../../store/store";
import InfiniteScroll from "react-infinite-scroll-component";
import FileUploadModal from "../../components/FileUploadModal/FileUploadModal";
import Button from "../../components/ui/button/Button";
import {
    selectProfilePhoto,
    fetchProfilePhoto,
    selectFullProfile,
} from "../../store/slices/profileSlice";
import NewsfeedFooter from "../../components/Footer/NewsfeedFooter";
import ScrollToTopButton from "../../components/ui/scroll-to-top-button/ScrollToTopButton";
import SortByDropdown from "../../components/ui/sort-posts-by-menu/SortPostsBy";
import LoadingOverlay from "../../components/ui/loading/LoadingOverlay";
import { PostCategory, POST_CATEGORIES, CATEGORY_DESCRIPTIONS } from "../../constants/categories";
import CategoriesSlide from "../../components/slides/NewsFeedPage/CategoriesSlide";
import { EmblaOptionsType } from "embla-carousel";
import { countryOptions, statesByCountry, getLabelForValue } from "../../utils/locationData";
import CreatePostModal from "../../components/modal/CreatePostModal/CreatePostModal";
import ProfileCard from "../../components/directory/ProfileCard/ProfileCard";
import CategoryFilters from "../../components/ui/CategoryFilters/CategoryFilters";
import DeletePostModal from "../../components/modal/DeletePostModal/DeletePostModal";
import MediaGrid from "../../components/media/MediaGrid/MediaGrid";
import EventsPreview from "../../components/events/EventsPreview/EventsPreview";
import TodaysNews from "../../components/news/TodaysNews";
import WelcomeBanner from "../../components/common/WelcomeBanner/WelcomeBanner";

const OPTIONS: EmblaOptionsType = {
    loop: true,
    align: "start",
};

// Added back the constant for number of posts to load
const numPostsPerLoad = 5;

interface Author {
    authorId: string;
    name: string;
    profileUrl: string;
    profileImageUrl: string;
}

interface MediaItem {
    mediaType: "image" | "video" | "pdf" | "gif" | "document" | "file";
    mediaUrl: string;
    thumbnailUrl?: string;
    fileName?: string;
    fileSize?: number;
    duration?: string;
}

// Interface for report details
interface ReportDetails {
    postId: string;
    reason: string;
    description: string;
    timestamp: string;
}

interface PostMetadata {
    postDate: string;
    visibility: string;
    hashtags: string[];
    category: PostCategory;
}

const NewsFeed: React.FC = () => {
    const { user, isAuthenticated } = useSelector((state: RootState) => state.auth);
    const userLocation = useSelector((state: RootState) => state.profile.location);
    const profile = useSelector(selectFullProfile);

    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const store = useStore<RootState>();

    // Get user profile photo
    const userProfilePhoto = useSelector(selectProfilePhoto);

    // Responsive breakpoint checks hooks
    const { isTablet, isDesktop, isMobile } = useResponsive();
    const { posts, loading, error, currentPage, totalPages } = useSelector(selectNewsfeed);
    const filteredPosts = useSelector(selectFilteredPosts); // Get filtered posts based on search
    const searchQuery = useSelector(selectSearchQuery); // Get current search query
    const activeCategories = useSelector(selectActiveCategories);

    // State for the new CreatePostModal
    const [isCreatePostModalOpen, setIsCreatePostModalOpen] = useState<boolean>(false);

    // State for DeletePostModal
    const [isDeleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
    const [postToDeleteId, setPostToDeleteId] = useState<string | null>(null);
    const [isDeleting, setIsDeleting] = useState<boolean>(false);

    // Report dialog state
    const [reportDialogOpen, setReportDialogOpen] = useState<boolean>(false);
    const [reportPostId, setReportPostId] = useState<string>("");
    const [reportReason, setReportReason] = useState<string>("");
    const [reportDescription, setReportDescription] = useState<string>("");
    const [reportsList, setReportsList] = useState<ReportDetails[]>([]);

    // Posting operation state
    const [isPosting, setIsPosting] = useState<boolean>(false);
    const [loadingMessage, setLoadingMessage] = useState<string>("Creating your post...");
    const [showSuccess, setShowSuccess] = useState<boolean>(false);
    const [successMessage, setSuccessMessage] = useState<string>("");

    useEffect(() => {
        // When profile data has been loaded (i.e., not loading and has an ID),
        // check if the initial setup is complete. If not, redirect the user.
        if (profile.loading === false && profile.id && profile.initialSetupComplete === false) {
            navigate("/profile-initial-setup");
        }
    }, [profile.loading, profile.id, profile.initialSetupComplete, navigate]);

    // Check for post modal flag on mount
    useEffect(() => {
        const shouldOpenModal = sessionStorage.getItem("openPostModal");
        if (shouldOpenModal === "true") {
            setIsCreatePostModalOpen(true);
            // Clear the flag
            sessionStorage.removeItem("openPostModal");
        }
    }, []);

    // Fetch profile photo if not already loaded
    useEffect(() => {
        if (isAuthenticated && user && user.id && !userProfilePhoto) {
            // // console.log("[NewsFeed] No profile photo found, fetching from API for user:", user.id);
            dispatch(fetchProfilePhoto(user.id));
        } else if (userProfilePhoto) {
            // // console.log("[NewsFeed] Profile photo already loaded:", userProfilePhoto);
        }
    }, [isAuthenticated, user, userProfilePhoto, dispatch]);

    useEffect(() => {
        if (isAuthenticated) {
            dispatch(fetchPosts({ page: 1, limit: numPostsPerLoad, sort: "desc" }));
        } else {
            navigate("/auth");
        }
    }, [dispatch, isAuthenticated, navigate, user]);

    // Add debugging effect to monitor posts array changes
    useEffect(() => {
        // // console.log("[NewsFeed] Posts array changed - Length:", posts.length);
        if (posts.length > 0) {
            // Log the first few post IDs to help track changes
            const postIds = posts.slice(0, Math.min(5, posts.length)).map((post) => post.id);
            // // console.log("[NewsFeed] First few post IDs:", postIds);
        }
    }, [posts]);

    // Function to load more posts when the user scrolls
    const fetchMoreData = () => {
        console.log(
            "[NewsFeed] fetchMoreData called - Current page:",
            currentPage,
            "Total pages:",
            totalPages,
        );
        // Check if there are more pages before fetching
        if (currentPage < totalPages) {
            // Get the current categories from Redux state to ensure consistency
            const currentCategories = selectActiveCategories(store.getState());

            // Dispatch fetchPosts with next page and current categories
            // // console.log("[NewsFeed] Fetching next page:", currentPage + 1);
            dispatch(
                fetchPosts({
                    page: currentPage + 1,
                    limit: numPostsPerLoad,
                    sort: "desc",
                    categories: currentCategories,
                }),
            );
        }
    };

    // Wrapper for the modal to call after its internal success animation completes
    const handleSuccessComplete = () => {
        setIsPosting(false);
        setShowSuccess(false);
        setIsCreatePostModalOpen(false);
    };

    const showSuccessAnimation = () => {
        setShowSuccess(true);
        setSuccessMessage("Post created successfully!");
        setTimeout(() => {
            // handleSuccessComplete will be called by LoadingOverlay when its animation finishes
            // or directly if there's no overlay animation delay for success.
            // For now, LoadingOverlay itself calls handleSuccessComplete prop.
            // If LoadingOverlay doesn't handle the delay, then call it here:
            // handleSuccessComplete();
            // However, the LoadingOverlay is designed to call it.
        }, 2000); // This timeout is for how long success message shows, not for modal close directly
    };

    // Show a loading screen if the profile is still loading or if setup is incomplete.
    // This prevents a "flash" of the newsfeed content before redirecting.
    const isProfileUnsettled =
        profile.loading || (profile.id && profile.initialSetupComplete === false);

    if (isProfileUnsettled) {
        return (
            <Container
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100vh", // Full viewport height
                }}
            >
                <CircularProgress />
                <Typography sx={{ mt: 2, color: "text.secondary" }}>Initializing...</Typography>
            </Container>
        );
    }

    const handlePostImageClick = (post: PostData, index: number) => {
        // No longer needed as PostImages handles this internally
    };

    // New handler for submitting post from the modal
    const handleModalPostSubmit = async (data: {
        postText: string;
        media: Array<{ mediaType: string; mediaUrl: string; thumbnailUrl?: string }>;
        category: PostCategory;
    }) => {
        console.log("[Newsfeed] Starting post submission...");
        const token = localStorage.getItem("accessToken");
        if (!data.postText.trim()) {
            alert("Post content cannot be empty.");
            return;
        }
        if (data.category === "All") {
            alert("Please select a specific category for your post");
            return;
        }
        if (!user || !token) {
            alert("You must be logged in to create a post.");
            return;
        }

        console.log("[Newsfeed] Setting loading state...");
        setLoadingMessage("Creating your post...");
        setIsPosting(true);
        setShowSuccess(false);

        const postDataObject = {
            author: {
                authorId: user.id,
                name: `${user.firstName || ""} ${user.lastName || ""}`.trim(),
                profileUrl: `/profile/${user.id}`,
                profileImageUrl: userProfilePhoto || "",
            },
            content: {
                text: data.postText,
                media: data.media.map(({ mediaType, mediaUrl }) => ({
                    mediaType,
                    mediaUrl,
                })),
            },
            postMetadata: {
                hashtags: [],
                category: data.category,
            },
        };

        try {
            console.log("[Newsfeed] Dispatching createPost action...");
            const result = await dispatch(createPost(postDataObject));
            console.log("[Newsfeed] CreatePost result:", result);

            // Check if the action was fulfilled
            if (result.meta.requestStatus === "fulfilled") {
                console.log("[Newsfeed] Post created successfully, showing success state");

                // First set isPosting to false to trigger the success state in LoadingOverlay
                setIsPosting(false);
                setShowSuccess(true);

                // Close the modal after the success animation
                setTimeout(() => {
                    console.log("[Newsfeed] Success animation complete, closing modal");
                    setShowSuccess(false);
                    setIsCreatePostModalOpen(false);
                }, 3000);
            } else {
                console.error("[Newsfeed] CreatePost action was not fulfilled:", result);
                setIsPosting(false);
                alert("Failed to create post. Please try again.");
            }
        } catch (error) {
            console.error("[Newsfeed] Failed to create post:", error);
            setIsPosting(false);
            alert("Failed to create post. Please try again.");
        }
    };

    const handleUpdatePost = (updatedPost: PostData) => {
        dispatch(updatePostAsync(updatedPost));
    };

    const handleDeleteRequest = (postId: string) => {
        setPostToDeleteId(postId);
        setDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!postToDeleteId) return;

        setIsDeleting(true);
        try {
            await dispatch(deletePostAsync(postToDeleteId)).unwrap();
            // Optionally show a success message/toast here
        } catch (error) {
            console.error("Failed to delete post:", error);
            // Optionally show an error message/toast here
        } finally {
            setIsDeleting(false);
            setDeleteModalOpen(false);
            setPostToDeleteId(null);
        }
    };

    // Post dropdown menu handlers
    const handleSharePost = (postId: string) => {
        // No loading overlay for share, just copy the link
        navigator.clipboard.writeText(`${window.location.origin}/newsfeed/${postId}`);
        alert("Post link copied to clipboard!");
    };

    const handleViewPostDetails = (postId: string) => {
        console.log(`[Newsfeed] Navigating to post details for postId: ${postId}`);
        const post = posts.find((p) => p.id === postId);
        if (post) {
            console.log(`[Newsfeed] Post details:`, post);
            console.log(`[Newsfeed] Comment count: ${post.comments.length}`);
        } else {
            console.error(`[Newsfeed] Post with ID ${postId} not found in posts array!`);
        }
        navigate(`/newsfeed/${postId}`);
    };

    // Handle post reporting
    const handleReportPost = (postId: string) => {
        setReportPostId(postId);
        setReportDialogOpen(true);
    };

    const handleReportSubmit = () => {
        // In a real application, this would send the report to an API
        if (reportReason) {
            // Close dialog first
            setReportDialogOpen(false);

            const newReport: ReportDetails = {
                postId: reportPostId,
                reason: reportReason,
                description: reportDescription,
                timestamp: new Date().toISOString(),
            };

            setReportsList([...reportsList, newReport]);

            // Reset form
            setReportReason("");
            setReportDescription("");

            // Show alert instead of loading overlay
            alert("Thank you for your report. We will review it shortly.");
        }
    };

    const handleLikePost = async (post: PostData, liked: boolean) => {
        try {
            if (!post) return;

            // No loading overlay for likes, just update immediately
            const likesCount = liked ? 1 : 0;
            const updatedPost = {
                ...post,
                engagement: {
                    ...post.engagement,
                    likesCount: post.engagement.likesCount + likesCount,
                },
            };

            await dispatch(updatePostAsync(updatedPost));
        } catch (error) {
            console.error("Failed to add like", error);
        }
    };

    const handleSortChange = (option: SortOption) => {
        dispatch(setSortOption(option));
    };

    const handleCategoryChange = (category: PostCategory | "All") => {
        dispatch(toggleCategory(category));

        // After toggling, fetch posts with the updated categories
        // We use getState() to access the latest state after toggling
        const updatedCategories = selectActiveCategories(store.getState());

        dispatch(
            fetchPosts({
                page: 1,
                limit: numPostsPerLoad,
                sort: "desc",
                categories: updatedCategories,
            }),
        );
    };

    // Handle feedback button click
    const handleFeedbackClick = () => {
        // Replace this URL with your actual Google Form URL
        const googleFormUrl =
            "https://docs.google.com/forms/d/e/1FAIpQLSeTuQwmG-JMFXfbdgLfxFIp2mKgjQs6RD9-pkjOevZYFN-BCw/viewform?usp=dialog";
        window.open(googleFormUrl, "_blank", "noopener,noreferrer");
    };

    const postsToRender = searchQuery ? filteredPosts : posts;

    return (
        <Container
            maxWidth={false} // Override the default size constraint
            className={classes.newsFeed}
            disableGutters={true} // Remove default padding completely
            sx={{
                overflowX: "hidden",
                width: "100%",
                boxSizing: "border-box",
                // Use padding directly in sx for better control
                paddingLeft: { xs: "12px", sm: "16px", md: "24px" },
                paddingRight: { xs: "12px", sm: "16px", md: "24px" },
            }}
        >
            {/* Loading Overlay */}
            {/* <LoadingOverlay
                isLoading={isPosting}
                message={loadingMessage}
                success={showSuccess}
                successMessage={successMessage}
                onSuccessComplete={handleSuccessComplete}
            /> */}

            <Grid
                container
                spacing={{ xs: 1, sm: 2, md: 3 }}
                sx={{
                    width: "100%",
                    margin: "0 auto",
                    boxSizing: "border-box",
                    maxWidth: { xs: "100%", sm: "100%", md: "1200px" },
                }}
            >
                {/* WELCOME BANNER SPANNING FULL WIDTH */}
                <Grid item xs={12} sx={{ order: { xs: 0, md: 0 }, width: "100%" }}>
                    <WelcomeBanner />
                </Grid>

                {/* Left Column - Profile Section */}
                <Grid
                    item
                    xs={12}
                    md={3}
                    sx={{
                        display: { xs: "flex", md: "block" },
                        flexDirection: { xs: "column", md: "column" },
                        order: { xs: 1, md: 1 },
                        boxSizing: "border-box",
                        width: "100%",
                        maxWidth: "100%",
                    }}
                >
                    {isAuthenticated && user ? (
                        <ProfileCard
                            user={{
                                id: user.id,
                                name: `${user.firstName || ""} ${user.lastName || ""}`.trim(),
                                profileImageUrl: userProfilePhoto || undefined,
                                title: profile.title || undefined,
                                location:
                                    userLocation && userLocation.state && userLocation.country
                                        ? `${getLabelForValue(
                                              userLocation.state,
                                              statesByCountry[userLocation.country] || [],
                                          )}, ${userLocation.country}`
                                        : "",
                                // Banner will use the default from the component
                            }}
                            disableHover={true}
                        />
                    ) : (
                        // show login prompt if not authenticated
                        <Paper className={classes.profileCard}>
                            <Typography variant="body2">Please Log in to make a post</Typography>
                        </Paper>
                    )}
                    <div className={classes.eventsPreviewSticky}>
                        <EventsPreview />
                    </div>
                </Grid>

                {/* Middle Column - Posts Section*/}
                <Grid
                    item
                    xs={12}
                    md={6}
                    sx={{
                        order: { xs: 3, md: 2 },
                    }}
                >
                    {/* New Post Input Trigger - REPLACES THE OLD INLINE FORM */}
                    {isAuthenticated && (
                        <Paper
                            className={classes.postInputTriggerContainer} // New main container class
                        >
                            {/* Top section: Avatar and "Start a post" field */}
                            <Box className={classes.triggerTopSection}>
                                <Avatar
                                    src={userProfilePhoto || undefined}
                                    sx={{
                                        width: 48, // Slightly larger avatar
                                        height: 48,
                                        cursor: "pointer",
                                        border: "1px solid var(--avatar-border-color)",
                                    }}
                                    onClick={() => user?.id && navigate(`/profile/${user.id}`)}
                                    aria-label="View your profile"
                                >
                                    {!userProfilePhoto && user?.firstName?.charAt(0)}
                                    {!userProfilePhoto && user?.lastName?.charAt(0)}
                                </Avatar>
                                <Box
                                    className={classes.startPostField}
                                    onClick={() => setIsCreatePostModalOpen(true)}
                                    aria-label="Start a post"
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) =>
                                        e.key === "Enter" && setIsCreatePostModalOpen(true)
                                    }
                                    sx={{ backgroundColor: "transparent" }}
                                >
                                    <Typography
                                        variant="body1"
                                        sx={{ color: "var(--text-secondary)" }}
                                    >
                                        Start a post ✍️
                                    </Typography>
                                </Box>
                            </Box>

                            {/* Bottom section: Action buttons */}
                            <Box className={classes.triggerActionButtons}>
                                <MuiButton
                                    startIcon={<PhotoOutlined />}
                                    className={classes.actionButton}
                                    onClick={() => setIsCreatePostModalOpen(true)}
                                    aria-label="Add a photo to your post"
                                >
                                    Photo
                                </MuiButton>
                                <MuiButton
                                    startIcon={<VideoCallOutlined />}
                                    className={classes.actionButton}
                                    onClick={() => setIsCreatePostModalOpen(true)}
                                    aria-label="Add a video to your post"
                                >
                                    Video
                                </MuiButton>
                                <MuiButton
                                    startIcon={<AttachFileOutlined />}
                                    className={classes.actionButton}
                                    onClick={() => setIsCreatePostModalOpen(true)}
                                    aria-label="Add a PDF to your post"
                                >
                                    PDF
                                </MuiButton>
                                <MuiButton
                                    startIcon={<AttachFileOutlined />}
                                    className={classes.actionButton}
                                    onClick={() => setIsCreatePostModalOpen(true)}
                                    aria-label="Add a document to your post"
                                >
                                    Document
                                </MuiButton>
                            </Box>
                        </Paper>
                    )}

                    {/* Sort posts by dropdown menu */}
                    <SortByDropdown onSortChange={handleSortChange} />

                    <div
                        style={{
                            borderRadius: "0.8rem",
                            backgroundColor: "var(--background-paper)",
                        }}
                    >
                        <Typography
                            variant="h6"
                            sx={{
                                marginBottom: "8px",
                                fontWeight: "bold",
                                color: "var(--text-primary)",
                            }}
                        >
                            Filter by Category
                        </Typography>
                        <CategoryFilters
                            categories={POST_CATEGORIES.filter(
                                (category) => category !== "General" && category !== "All",
                            )}
                            activeCategories={activeCategories}
                            onCategoryToggle={handleCategoryChange}
                        />
                    </div>

                    {/* Posts Feed */}
                    <Box className={classes.postsContainer}>
                        {loading && posts.length === 0 ? (
                            // Show a loading text if posts haven't loaded at all
                            <Typography align="center">Loading...</Typography>
                        ) : error ? (
                            <Typography align="center">{error}</Typography>
                        ) : (
                            <InfiniteScroll
                                style={{ overflow: "visible" }}
                                dataLength={posts.length}
                                next={fetchMoreData} // Called when scroll reaches the bottom
                                hasMore={currentPage < totalPages} // Check if there are more pages
                                loader={
                                    <Box display="flex" justifyContent="center" mt={2}>
                                        <CircularProgress />
                                    </Box>
                                }
                                endMessage={
                                    <Box textAlign="center" mt={2}>
                                        <Typography variant="h6">
                                            🎊 You&apos;ve reached the end of the feed! 🎊
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            Want to see more? Engage with the community by sharing
                                            your thoughts or checking back later for new posts.
                                        </Typography>
                                        <Button
                                            variant="primary"
                                            fill="solid"
                                            size="medium"
                                            label="Create a Post"
                                            onClick={() => window.scrollTo(0, 0)}
                                            disabled={isPosting}
                                        />
                                    </Box>
                                }
                            >
                                {postsToRender.length > 0 ? (
                                    postsToRender.map((post: PostData) => {
                                        const { comments, ...restOfPost } = post;
                                        return (
                                            <div key={post.id}>
                                                <Post
                                                    {...restOfPost}
                                                    media={post.content.media?.map((item) => ({
                                                        ...item,
                                                        mediaType: item.mediaType as
                                                            | "image"
                                                            | "video",
                                                    }))}
                                                    comments={comments}
                                                    onShare={() => handleSharePost(post.id)}
                                                    onViewDetails={() =>
                                                        handleViewPostDetails(post.id)
                                                    }
                                                    onReport={() => handleReportPost(post.id)}
                                                    onDelete={() => handleDeleteRequest(post.id)}
                                                    onLike={handleLikePost}
                                                    onImageClick={(index) =>
                                                        handlePostImageClick(post, index)
                                                    }
                                                    onComment={() => handleViewPostDetails(post.id)}
                                                />
                                            </div>
                                        );
                                    })
                                ) : searchQuery ? (
                                    <Box textAlign="center" mt={4} mb={4}>
                                        <Typography variant="h6">
                                            No posts match your search
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            Try different keywords or check back later for new
                                            content.
                                        </Typography>
                                    </Box>
                                ) : (
                                    <Typography align="center">No posts available</Typography>
                                )}
                            </InfiniteScroll>
                        )}

                        {/* Manual load more button */}
                        <Box display="flex" justifyContent="center" mt={2}>
                            {currentPage < totalPages && (
                                <Button
                                    variant="primary"
                                    fill="solid"
                                    size="medium"
                                    label="Load More Posts"
                                    onClick={fetchMoreData}
                                    disabled={isPosting}
                                />
                            )}
                        </Box>
                    </Box>
                </Grid>

                {/* Right Column - With separate scrolling sections */}
                <Grid
                    item
                    xs={12}
                    md={3}
                    sx={{
                        display: { xs: isMobile ? "none" : "block", md: "block" },
                        order: { xs: 2, md: 3 },
                    }}
                >
                    <div className={classes.rightSidebarContainer}>
                        {/* News section - this scrolls with the page */}
                        <TodaysNews />

                        {/* About MAX Aspire section - this is sticky */}
                        {/* <Paper className={`${classes.exploreSection}`}>
              <Typography variant="h6">About MAX Aspire</Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                MAX Aspire is a program aimed at supporting Muslim professionals in advancing their careers.
              </Typography>
            </Paper> */}

                        {/* NewsfeedFooter - this is also sticky */}
                        <div className={classes.stickyFooter}>
                            <NewsfeedFooter />

                            {/* Sticky Feedback Button - now part of the sticky footer */}
                            <Box
                                className={classes.feedbackButton}
                                onClick={handleFeedbackClick}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => e.key === "Enter" && handleFeedbackClick()}
                                aria-label="Share feedback about MAX Aspire"
                            >
                                <FeedbackOutlined className={classes.feedbackIcon} />
                                <Typography variant="body2" className={classes.feedbackText}>
                                    Help us make Aspire better
                                </Typography>
                            </Box>
                        </div>
                    </div>
                </Grid>
            </Grid>

            {/* Scroll to top button - now consistently positioned via CSS */}
            <ScrollToTopButton />

            {/* Report Dialog */}
            <Dialog open={reportDialogOpen} onClose={() => setReportDialogOpen(false)}>
                <DialogTitle>Report Post</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Please tell us why you&apos;re reporting this post. Your report is
                        anonymous.
                    </DialogContentText>
                    <FormControl fullWidth margin="normal">
                        <InputLabel id="report-reason-label">Reason</InputLabel>
                        <Select
                            labelId="report-reason-label"
                            value={reportReason}
                            label="Reason"
                            onChange={(e: SelectChangeEvent) => setReportReason(e.target.value)}
                        >
                            <MenuItem value="inappropriate">Inappropriate Content</MenuItem>
                            <MenuItem value="spam">Spam</MenuItem>
                            <MenuItem value="harassment">Harassment</MenuItem>
                            <MenuItem value="false-info">False Information</MenuItem>
                            <MenuItem value="other">Other</MenuItem>
                        </Select>
                    </FormControl>
                    <TextField
                        autoFocus
                        margin="normal"
                        label="Additional Details (Optional)"
                        fullWidth
                        multiline
                        rows={4}
                        value={reportDescription}
                        onChange={(e) => setReportDescription(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        label="Cancel"
                        onClick={() => setReportDialogOpen(false)}
                        disabled={isPosting}
                    />
                    <Button
                        label="Submit Report"
                        onClick={handleReportSubmit}
                        disabled={!reportReason || isPosting}
                        variant="primary"
                    />
                </DialogActions>
            </Dialog>

            {/* NEW: Render CreatePostModal */}
            <CreatePostModal
                isOpen={isCreatePostModalOpen}
                onClose={() => setIsCreatePostModalOpen(false)}
                user={user}
                userProfilePhoto={userProfilePhoto || null}
                onAddPost={handleModalPostSubmit}
                isPostingInProgress={isPosting}
            />

            {/* NEW: Render DeletePostModal */}
            <DeletePostModal
                isOpen={isDeleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                onConfirm={handleConfirmDelete}
                isDeleting={isDeleting}
            />
        </Container>
    );
};

export default NewsFeed;
